import { useAtomValue } from 'jotai';
import { recorderNetworkLogsAtom } from '~/atoms/recorder-atoms';
import { useState, useMemo } from 'react';
import { useDataTable } from '~/components/tables/common/useDataTable';
import { DataTable } from '~/components/tables/common/DataTable';
import { Text, TextInput } from '@mantine/core';
import { networkColumns } from '~/components/tables/network/networkColumns';
import { NetworkDetailsView } from '~/components/browser/status-panel/panels/NetworkDetailsPanel';
import type { NetworkLog } from '~/common/vm/protocol';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';

function extractPathFromUrl(url: string) {
  try {
    const urlObj = new URL(url);
    if (urlObj.pathname === '/') {
      return urlObj.hostname;
    }

    const pathSegments = urlObj.pathname.split('/').filter(Boolean);
    return pathSegments.at(-1) ?? urlObj.hostname;
  } catch {
    return url;
  }
}

export function NetworkPanel({
  showSearch,
  setShowSearch,
}: {
  showSearch: boolean;
  setShowSearch: (show: boolean) => void;
}) {
  const logsData = useAtomValue(recorderNetworkLogsAtom);
  const [selectedLog, setSelectedLog] = useState<NetworkLog>();
  const [searchQuery, setSearchQuery] = useState('');
  // Debounce search query to prevent excessive re-renders
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);

  const filteredLogsData = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return logsData;
    }

    const query = debouncedSearchQuery.toLowerCase();
    return logsData.filter((log) => {
      const name = extractPathFromUrl(log.url);
      return name.toLowerCase().includes(query);
    });
  }, [logsData, debouncedSearchQuery]);

  const table = useDataTable({
    columns: networkColumns(setSelectedLog),
    data: filteredLogsData,
    enablePagination: false,
    enableQueryParam: false,
  });

  return selectedLog ?
      <NetworkDetailsView
        log={selectedLog}
        onBack={() => setSelectedLog(undefined)}
      />
    : <div className="relative flex h-full flex-col border-t border-neutral-200 dark:border-neutral-700">
        {!!showSearch && (
          <TextInput
            type="text"
            placeholder="Search network requests..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            w={200}
            size="xs"
            autoFocus
            className="absolute right-6 top-2 z-10 shadow-lg"
            leftSection={
              <IconSearch
                size={14}
                className="text-neutral-500"
              />
            }
            rightSection={
              <IconX
                size={14}
                className="cursor-pointer text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
                onClick={() => {
                  setSearchQuery('');
                  setShowSearch(false);
                }}
              />
            }
          />
        )}
        <DataTable
          table={table}
          className="text-xs"
          classNames={{
            header:
              'bg-[var(--mantine-color-body)] text-[var(--mantine-color-dimmed)] py-1.5 [&:not(:last-child)]:border-r [&:not(:last-child)]:border-neutral-200 [&:not(:last-child)]:dark:border-neutral-700',
            cell: 'py-1 [&:not(:last-child)]:border-r [&:not(:last-child)]:border-neutral-200 [&:not(:last-child)]:dark:border-neutral-700',
          }}
          options={{
            enableVirtualization: true,
            estimatedRowHeight: 27,
            overscan: 5,
          }}
          emptyMessage={
            <div className="flex items-center justify-center py-8">
              <Text
                c="dimmed"
                size="sm"
              >
                {debouncedSearchQuery.trim() ?
                  `No network requests found matching "${debouncedSearchQuery}"`
                : 'No network requests captured yet'}
              </Text>
            </div>
          }
        />
      </div>;
}
